import { useCallback, useEffect, useEffectEvent, useMemo } from 'react';
import { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

import { ILocationFragment, PaginatedLocationsDocument } from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { usePagination } from 'common/hooks/usePagination';
import { useUrlFilterParams } from 'common/hooks/useUrlFilterParams';

export const useLocationList = (filterParamsInUrl = true) => {
    const { showApolloError } = useApolloError();

    const { filterParams, setFilterParam } = useUrlFilterParams(
        'location',
        filterParamsInUrl
    );

    const searchQueryParam = filterParams.q || '';

    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);

    const variables = useMemo(
        () => ({
            offset: paginationSettings.offset,
            first: paginationSettings.first,
            q: searchQueryParam,
        }),
        [paginationSettings.offset, paginationSettings.first, searchQueryParam]
    );

    const { loading, data, error } = useQuery(PaginatedLocationsDocument, {
        variables,
    });

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    const locationsObject = data?.paginatedLocations;
    const locationsCount = locationsObject?.count;
    const locations = useMemo(
        () =>
            (locationsObject?.edges
                .map((edge) => edge?.node || null)
                .filter(Boolean) as ILocationFragment[]) || [],
        [locationsObject?.edges]
    );

    useEffect(() => {
        if (loading || !initializePagination) return;

        initializePagination(locationsCount || 0);
    }, [locationsCount, loading, initializePagination]);

    const onSearch = useCallback(
        (searchValue: string) => {
            setFilterParam('q', searchValue);
        },
        [setFilterParam]
    );

    return {
        locations,
        locationsCount,
        loading,
        paginationSettings,
        filterValues: { q: searchQueryParam },
        setPage,
        onSearch,
    };
};
